import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  InputAdornment,
  IconButton,
  Popper,
  Chip,
  Stack,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import proveedorService, { Proveedor } from '../services/proveedorService';
import { formatCuitDniDisplay } from '../utils/formatters';
import {
  labelTipoIvaCliente,
  chipColorTipoIvaCliente,
  chipColorCondVtaCliente,
  clienteChipSx,
  formatDomicilioFacturacionCliente,
  readTipoIvaCliente,
} from '../pages/facturaventa/pos/clienteNombre';
const PAGE_SIZE = 20;
const DEBOUNCE_MS = 800;

export interface ProveedorInlineSearchFieldProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  displayValue?: string;
  onSelect: (proveedor: { nroProveedor: number; nombre?: string }) => void;
  onOpenModal?: () => void;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
}

const esEntradaNumerica = (texto: string) => {
  const t = texto.trim();
  if (!t) return false;
  return t.replace(/\d/g, '') === '';
};

const debeBuscar = (trimmed: string) => trimmed.length > 0;

const ProveedorInlineSearchField: React.FC<ProveedorInlineSearchFieldProps> = ({
  label = 'Proveedor',
  placeholder = 'Número, nombre o CUIT',
  helperText,
  displayValue,
  onSelect,
  onOpenModal,
  disabled = false,
  required = false,
  size = 'medium',
}) => {
  const [term, setTerm] = useState(displayValue ?? '');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Proveedor[]>([]);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const userEditedRef = useRef(false);
  const termRef = useRef(term);
  termRef.current = term;
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const resetBusqueda = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  useEffect(() => {
    if (!isFocused && displayValue !== undefined) {
      userEditedRef.current = false;
      setTerm((prev) => (prev === displayValue ? prev : displayValue));
      resetBusqueda();
      setLoading(false);
      searchRequestIdRef.current += 1;
    }
  }, [displayValue, isFocused, resetBusqueda]);

  const buscarPorNroExacto = useCallback(async (soloDigitos: string): Promise<Proveedor | null> => {
    if (soloDigitos.length === 0) return null;
    const nro = Number(soloDigitos);
    if (!Number.isFinite(nro) || nro <= 0) return null;
    try {
      return await proveedorService.getProveedor(nro);
    } catch {
      return null;
    }
  }, []);

  const buscarProveedores = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!debeBuscar(trimmed)) {
        resetBusqueda();
        setLoading(false);
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setLoading(true);

      try {
        const compact = trimmed.replace(/\s+/g, '');
        if (compact !== '' && /^\d+$/.test(compact)) {
          const porNro = await buscarPorNroExacto(compact);
          if (requestId !== searchRequestIdRef.current) return;
          if (porNro) {
            setSuggestions([porNro]);
            setOpen(true);
            setHighlightedIndex(0);
            itemRefs.current = [];
          } else {
            resetBusqueda();
          }
          return;
        }

        const res = await proveedorService.getProveedores({
          searchTerm: trimmed,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
        });

        if (requestId !== searchRequestIdRef.current) return;

        let items = res.items ?? [];

        if (esEntradaNumerica(trimmed)) {
          const porNro = await buscarPorNroExacto(trimmed.replace(/\D/g, ''));
          if (requestId !== searchRequestIdRef.current) return;
          if (porNro && !items.some((p) => p.nroProveedor === porNro.nroProveedor)) {
            items = [porNro, ...items];
          }
        }

        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlightedIndex(items.length > 0 ? 0 : -1);
        itemRefs.current = [];
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        resetBusqueda();
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [resetBusqueda, buscarPorNroExacto]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!isFocused || !userEditedRef.current) {
      return;
    }

    const pendingTerm = term;
    debounceRef.current = setTimeout(() => {
      if (termRef.current !== pendingTerm) return;
      void buscarProveedores(pendingTerm);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarProveedores, isFocused]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setAnchorWidth(containerRef.current.offsetWidth);
    }
  }, [open, suggestions.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelect = (proveedor: Proveedor) => {
    userEditedRef.current = false;
    onSelect(proveedor);
    resetBusqueda();
    setLoading(false);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      userEditedRef.current = false;
      if (displayValue !== undefined) setTerm(displayValue);
      setLoading(false);
      return;
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const proveedor = suggestions[idx];
      if (proveedor) handleSelect(proveedor);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (displayValue !== undefined && !open) {
      userEditedRef.current = false;
      setTerm(displayValue);
      setLoading(false);
      resetBusqueda();
    }
  };

  const resolvedHelperText =
    helperText ??
    (onOpenModal
      ? 'Escriba para buscar o use la lupa para el listado completo'
      : 'Escriba para buscar');

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        size={size}
        label={label || undefined}
        placeholder={placeholder}
        value={term}
        onChange={(e) => {
          userEditedRef.current = true;
          setTerm(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
          if (suggestions.length > 0) setOpen(true);
        }}
        onBlur={handleBlur}
        disabled={disabled}
        required={required}
        helperText={resolvedHelperText || undefined}
        InputLabelProps={size === 'small' ? { shrink: true } : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: '#ffffff',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={20} sx={{ mr: 0.5 }} />
              ) : null}
              {onOpenModal ? (
                <IconButton
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onOpenModal}
                  edge="end"
                  color="primary"
                  size={size}
                  title="Buscar en listado completo"
                  disabled={disabled}
                  sx={{ borderRadius: 1.5 }}
                >
                  <SearchIcon fontSize={size === 'small' ? 'small' : 'medium'} />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={open && suggestions.length > 0}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        style={{ width: anchorWidth > 0 ? anchorWidth : undefined }}
      >
        <Paper
          ref={listRef}
          elevation={6}
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            borderRadius: 2,
            width: '100%',
          }}
        >
          <List dense disablePadding>
            {suggestions.map((p, idx) => {
              const tipoIva = readTipoIvaCliente(p);
              const doc = formatCuitDniDisplay(p.cuit, p.dni, '');
              const domicilio = formatDomicilioFacturacionCliente(p);
              const condVta = String(p.condVta ?? '').trim();
              return (
                <ListItem key={p.nroProveedor} disablePadding divider={idx < suggestions.length - 1}>
                  <ListItemButton
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    selected={idx === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(p)}
                    sx={{ py: 0.85, px: 1.5 }}
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 48, fontFamily: 'monospace', flexShrink: 0 }}
                          >
                            #{p.nroProveedor}
                          </Typography>
                          <Typography variant="body2" noWrap flex={1}>
                            {p.nombre}
                          </Typography>
                          {tipoIva ? (
                            <Chip
                              label={labelTipoIvaCliente(tipoIva)}
                              size="small"
                              color={chipColorTipoIvaCliente(tipoIva)}
                              sx={clienteChipSx}
                            />
                          ) : null}
                        </Box>
                      }
                      secondary={
                        doc || domicilio || condVta ? (
                          <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                            {doc || condVta ? (
                              <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0.75}>
                                {doc ? (
                                  <Typography variant="caption" color="text.secondary" component="span">
                                    {doc}
                                  </Typography>
                                ) : null}
                                {condVta ? (
                                  <Chip
                                    label={condVta}
                                    size="small"
                                    color={chipColorCondVtaCliente(condVta)}
                                    sx={clienteChipSx}
                                  />
                                ) : null}
                              </Stack>
                            ) : null}
                            {domicilio ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                                sx={{ display: 'block', lineHeight: 1.25, whiteSpace: 'normal' }}
                              >
                                {domicilio}
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : null
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}          </List>
        </Paper>
      </Popper>
    </Box>
  );
};
export default ProveedorInlineSearchField;
